import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { API_BASE_URL } from '../config/apiConfig';

// ============================================================================
// SWR GLOBAL CONFIG - Applied to all SWR hooks
// ============================================================================

// Set global SWR configuration
const globalSWRConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  refreshInterval: 0, // Disable automatic refresh by default
  dedupingInterval: 2000, // Dedupe requests within 2 seconds
  errorRetryCount: 3,
  errorRetryInterval: 5000,
  onError: (error, key) => {
    // Don't log 404 errors for optional endpoints (like /deviation)
    if (error.status === 404) {
      // Silently ignore 404 errors - endpoint might not exist
      return;
    }
    
    console.error('SWR Error:', error, 'Key:', key);
    
    // Handle authentication errors globally
    if (error.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  },
};

// ============================================================================
// SIMPLE API HELPERS - Inlined for simplicity
// ============================================================================

// Debug logging for API URL (can be removed in production)
console.log('🔧 API Configuration:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_HOST: process.env.REACT_APP_HOST,
  REACT_APP_BACKEND_PORT: process.env.REACT_APP_BACKEND_PORT,
  Final_API_BASE_URL: API_BASE_URL
});

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;
  
  if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
    try {
      const parsedUser = JSON.parse(savedUser);
      userId = parsedUser.id?.toString() || null;
    } catch (parseError) {
      userId = null;
    }
  }
  
  const headers = {
    'Content-Type': 'application/json',
    ...(userId && { 'user-id': userId }) // Only include if userId exists
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
};

const createSWRKey = (endpoint, params = {}) => {
  if (!params || Object.keys(params).length === 0) {
    return endpoint;
  }
  const queryString = new URLSearchParams(params).toString();
  return `${endpoint}?${queryString}`;
};

// ============================================================================
// GENERIC FETCHER - Works for any endpoint
// ============================================================================

const extractErrorPayload = async (response) => {
  try {
    return await response.json();
  } catch (_) {
    return { message: response.statusText || 'Network error' };
  }
};

const normalizeErrorMessage = (payload, fallback) => {
  if (!payload) return fallback;
  let msg = payload.message;
  if (Array.isArray(msg)) {
    msg = msg.join(', ');
  }
  if (!msg && payload.errorCode === 'P2002') {
    const fields = Array.isArray(payload?.meta?.target)
      ? payload.meta.target.join(', ')
      : (payload?.meta?.target || 'field');
    msg = `Unique constraint violation on ${fields}`;
  }
  return msg || fallback;
};

export const fetcher = async (url) => {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    // Handle 404 errors gracefully for optional endpoints
    if (response.status === 404) {
      // Return empty array for lookup endpoints that don't exist
      if (url.includes('/deviation') || url.includes('/risk-assessment')) {
        return [];
      }
    }
    
    const error = new Error('Fetch failed');
    error.info = await extractErrorPayload(response);
    error.status = response.status;
    error.message = normalizeErrorMessage(error.info, error.message);
    // Bubble structured extras for UI rendering
    error.errorCode = error?.info?.errorCode;
    error.details = error?.info?.details;
    error.meta = error?.info?.meta;
    
    // Handle authentication errors
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  }

  return response.json();
};

// ============================================================================
// GENERIC MUTATION FETCHER - Works for any endpoint and method
// ============================================================================

const mutationFetcher = async (url, { arg }) => {
  const { method = 'POST', data, fieldsToRemove = [], url: customUrl, ...options } = arg;
  const finalUrl = customUrl || url;
  
  // Remove unwanted fields if specified
  let submitData = data;
  if (data && fieldsToRemove.length > 0) {
    submitData = { ...data };
    fieldsToRemove.forEach(field => {
      delete submitData[field];
    });
  }
  
  // Get user ID from localStorage for audit tracking
  const savedUser = localStorage.getItem('user');
  let userId = null;
  
  console.log('mutationFetcher - Debug User ID extraction:', {
    savedUser,
    savedUserType: typeof savedUser,
    savedUserLength: savedUser?.length
  });
  
  if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
    try {
      const parsedUser = JSON.parse(savedUser);
      userId = parsedUser.id?.toString() || null;
      console.log('mutationFetcher - Parsed user:', {
        parsedUser,
        userId,
        userIdType: typeof userId
      });
    } catch (parseError) {
      console.error('mutationFetcher - Error parsing user:', parseError);
      userId = null;
    }
  } else {
    console.log('mutationFetcher - No valid user data found');
  }

  // Get local IP address for audit tracking (with timeout)
  let localIP = null;
  try {
    const { getLocalIPAddress } = await import('../utils/getLocalIP');
    localIP = await Promise.race([
      getLocalIPAddress(),
      new Promise((resolve) => setTimeout(() => resolve(null), 2000)) // 2 second timeout
    ]);
  } catch (error) {
    console.warn('Failed to get local IP address:', error);
    localIP = null;
  }
  
  // Get auth headers separately to avoid conflicts
  const authHeaders = getAuthHeaders();
  
  console.log('mutationFetcher - Auth headers from getAuthHeaders():', authHeaders);
  console.log('mutationFetcher - User ID before setting headers:', userId);
  
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(localIP && { 'X-Local-IP': localIP }), // Local IP address
      ...authHeaders,
      ...options.headers,
    },
    ...options,
  };

  // Always set user-id header last to ensure it's not overridden
  if (userId) {
    config.headers['user-id'] = userId;
  }

  console.log('mutationFetcher - Final headers being sent:', {
    'user-id': userId,
    'X-Local-IP': localIP,
    'Content-Type': 'application/json',
    allHeaders: config.headers
  });

  // Check if user is logged in for operations that require it
  // Exclude login and other auth endpoints that don't require existing authentication
  const authEndpoints = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
  const isAuthEndpoint = authEndpoints.some(endpoint => finalUrl.includes(endpoint));
  
  if (!userId && !isAuthEndpoint && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
    console.error('mutationFetcher - User ID is required for this operation but not found');
    throw new Error('User must be logged in to perform this operation');
  }

  if (submitData) {
    if (submitData instanceof FormData) {
      delete config.headers['Content-Type'];
      config.body = submitData;
    } else {
      config.body = JSON.stringify(submitData);
    }
  }

  const response = await fetch(`${API_BASE_URL}${finalUrl}`, config);

  if (!response.ok) {
    const error = new Error('Mutation failed');
    error.info = await extractErrorPayload(response);
    error.status = response.status;
    error.message = normalizeErrorMessage(error.info, error.message);
    // Bubble structured extras for UI rendering
    error.errorCode = error?.info?.errorCode;
    error.details = error?.info?.details;
    error.meta = error?.info?.meta;
    throw error;
  }

  return response.json();
};

// ============================================================================
// DYNAMIC SWR HOOKS - Work for ANY entity
// ============================================================================

/**
 * Generic hook to fetch a list of entities
 * @param {string} endpoint - API endpoint (e.g., '/user', '/department')
 * @param {object} params - Query parameters
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useEntities = (endpoint, params = {}) => {
  // Create cache key with params
  const key = endpoint ? createSWRKey(endpoint, params) : null;
  const { data, error, mutate } = useSWR(key, fetcher, globalSWRConfig);

  return {
    data: Array.isArray(data) ? data : [],
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

/**
 * Flexible list hook that supports array or { data: [] } API shapes
 */
export const useFlexibleList = (endpoint, params = {}) => {
  const key = endpoint ? createSWRKey(endpoint, params) : null;
  const { data, error, mutate } = useSWR(key, fetcher, globalSWRConfig);

  const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  return {
    data: list,
    raw: data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

/**
 * Generic hook to fetch a single entity by ID
 * @param {string} endpoint - API endpoint (e.g., '/user', '/department')
 * @param {number|string} id - Entity ID
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useEntity = (endpoint, id) => {
  const url = id ? `${endpoint}/${id}` : null;
  const { data, error, mutate } = useSWR(url, fetcher, globalSWRConfig);

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

/**
 * Generic hook to create an entity
 * @param {string} endpoint - API endpoint (e.g., '/user', '/department')
 * @returns {function} create function that takes (data, fieldsToRemove)
 */
export const useCreateEntity = (endpoint) => {
  const { trigger } = useSWRMutation(endpoint, mutationFetcher);

  return (data, fieldsToRemove = []) => trigger({ 
    method: 'POST', 
    data, 
    fieldsToRemove 
  });
};

/**
 * Generic hook to update an entity
 * @param {string} endpoint - API endpoint (e.g., '/user', '/department')
 * @returns {function} update function that takes (id, data, fieldsToRemove)
 */
export const useUpdateEntity = (endpoint) => {
  const { trigger } = useSWRMutation(endpoint, mutationFetcher);

  return (id, data, fieldsToRemove = []) => trigger({ 
    method: 'PATCH', 
    data, 
    fieldsToRemove,
    url: `${endpoint}/${id}`
  });
};

/**
 * Settings → Reference Documents: silent PATCH (no updatedAt/updatedBy/audit on backend).
 */
export const useUpdateReferenceDocumentSilent = (endpoint) => {
  const { trigger } = useSWRMutation(endpoint, mutationFetcher);

  return (id, referenceDocument) =>
    trigger({
      method: 'PATCH',
      data: { referenceDocument },
      url: `${endpoint}/${id}/reference-document`,
    });
};

/**
 * Generic hook to delete an entity
 * @param {string} endpoint - API endpoint (e.g., '/user', '/department')
 * @returns {function} delete function that takes (id)
 */
export const useDeleteEntity = (endpoint) => {
  const { trigger } = useSWRMutation(endpoint, mutationFetcher);

  return (id) => trigger({ 
    method: 'DELETE',
    url: `${endpoint}/${id}`
  });
};

// ============================================================================
// AUTH HOOKS - Special case for authentication
// ============================================================================

export const useLogin = () => {
  const { trigger, isMutating, error } = useSWRMutation('/auth/login', mutationFetcher);

  const login = async (credentials) => {
    const result = await trigger({ data: credentials });
    
    if (result.access_token) {
      localStorage.setItem('authToken', result.access_token);
      localStorage.setItem('user', JSON.stringify(result.user));
    }
    
    return result;
  };

  return {
    login,
    isLoading: isMutating,
    error,
  };
};

export const useAuth = () => {
  // Since there's no profile endpoint, we'll use session validation
  const { data: sessionValid, error, mutate } = useSWR('/auth/validate-session', fetcher, globalSWRConfig);
  
  return {
    user: sessionValid ? JSON.parse(localStorage.getItem('user') || '{}') : null,
    isLoading: !error && !sessionValid,
    isError: error,
    mutate,
  };
};